import { swatchContrast } from '../lib/contrast';
import { harmonies, isAchromatic } from '../lib/harmony';
import { CopyButton } from './CopyButton';
import styles from './HarmonyPanel.module.css';

interface HarmonyPanelProps {
  /** The currently selected base color, or null before any image is loaded. */
  baseColor: string | null;
}

/** A single harmony color: flat color field, hex label, and WCAG contrast badge. */
function HarmonySwatch({ color }: { color: string }) {
  const { textColor, ratio, rating } = swatchContrast(color);
  const textName = textColor === '#000000' ? 'black' : 'white';
  return (
    <div className={styles.swatch} style={{ background: color, color: textColor }}>
      <div className={styles.top}>
        <span className={styles.hex}>{color}</span>
        <CopyButton value={color} className={styles.copy} />
      </div>
      <span
        className={styles.badge}
        title={`Contrast ${ratio.toFixed(2)}:1 with ${textName} text`}
      >
        {rating} · {ratio.toFixed(1)}:1
      </span>
    </div>
  );
}

/**
 * Three harmony rows (complementary, analogous, triadic) derived from the base
 * color. For a low-saturation base the hue is undefined and harmonies collapse,
 * so we show the base color alone with a note rather than degenerate duplicates.
 */
export function HarmonyPanel({ baseColor }: HarmonyPanelProps) {
  if (!baseColor) return null;

  if (isAchromatic(baseColor)) {
    return (
      <section aria-label="Color harmonies" className={styles.harmonies}>
        <p className={styles.note} role="note">
          Low-saturation base color — harmonies are limited because the hue is undefined.
        </p>
        <div className={styles.row}>
          <HarmonySwatch color={baseColor} />
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Color harmonies" className={styles.harmonies}>
      {harmonies(baseColor).map((row) => (
        <div key={row.kind} className={styles.harmony}>
          <h2 className={styles.label}>{row.label}</h2>
          <div className={styles.row}>
            {row.colors.map((color, i) => (
              <HarmonySwatch key={`${color}-${i}`} color={color} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
